/**
 * ─── Pawnshop Schema — DDD Aggregate Design ─────────────────
 * Pawn Shop Management System with double-entry ledger.
 *
 * Aggregate Root: PawnTicket
 * Entities: PawnCustomer, PawnItem, PawnPayment, AuctionItem
 * Value Objects: Money, InterestRate, MaturityDate, AppraisalValue
 *
 * Double-Entry Flow:
 *   Pawn:  DR Customer Loan Receivable  / CR Cash
 *   Interest: DR Customer / CR Interest Income
 *   Redeem: DR Cash / CR Customer Loan Receivable
 *   Auction: DR Cash / CR Loan Receivable + CR Customer Surplus
 *
 * Metadata-Driven: interest rates, grace periods, appraisal
 * categories, auction rules — all per-tenant JSONB config.
 */

import { z } from 'zod'
import { notesSchema, createQuerySchema, createUpdateSchema, emailSchema, phoneSchema } from '@repo/core'

// ═══════════════════════════════════════════════════════════════
// Pawn Customer (Pawner)
// ═══════════════════════════════════════════════════════════════

export type PawnCustomerType = 'individual' | 'business'
export type PawnCustomerStatus = 'active' | 'blocked' | 'blacklisted'

export interface PawnCustomer {
  id: string
  tenantId: string
  branchId?: string
  customerCode: string
  firstName: string
  lastName: string
  fullName: string
  customerType: PawnCustomerType
  phone: string
  email?: string
  address: string
  validIdType?: string         // Driver's License, Passport, UMID, etc.
  validIdNumber?: string
  validIdPhotoUrl?: string
  status: PawnCustomerStatus
  totalPawnedCount: number
  totalRedeemedCount: number
  totalAuctionedCount: number
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const PawnCustomerTypeSchema = z.enum(['individual', 'business'])
export const PawnCustomerStatusSchema = z.enum(['active', 'blocked', 'blacklisted'])

export const CreatePawnCustomerSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  customerType: PawnCustomerTypeSchema.default('individual'),
  phone: phoneSchema,
  email: emailSchema.optional(),
  address: z.string().min(1).max(500),
  validIdType: z.string().max(100).optional(),
  validIdNumber: z.string().max(100).optional(),
  notes: notesSchema.optional(),
})

export const CUSTOMER_TYPE_LABELS: Record<PawnCustomerType, string> = {
  individual: 'Individual', business: 'Business',
}
export const CUSTOMER_STATUS_LABELS: Record<PawnCustomerStatus, string> = {
  active: 'Active', blocked: 'Blocked', blacklisted: 'Blacklisted',
}

// ═══════════════════════════════════════════════════════════════
// Pawn Item Category (metadata-driven)
// ═══════════════════════════════════════════════════════════════

export type ItemCategory = 'jewelry' | 'gadget' | 'appliance' | 'vehicle' | 'firearm' | 'antique' | 'other'
export type ItemCondition = 'excellent' | 'good' | 'fair' | 'poor'
export type ItemStatus = 'pawned' | 'redeemed' | 'auctioned' | 'expired' | 'pulled_out'

export interface PawnItem {
  id: string
  tenantId: string
  branchId?: string
  ticketId?: string             // Linked pawn ticket
  itemCode: string
  category: ItemCategory
  description: string
  brand?: string
  model?: string
  serialNumber?: string
  condition: ItemCondition
  appraisalValue: number         // Expert appraisal
  pawnValue: number              // Loan amount granted (typically 50-80% of appraisal)
  weight?: number               // For jewelry (grams)
  karat?: number                // For gold (10, 14, 18, 21, 24)
  status: ItemStatus
  pawnDate?: number
  maturityDate?: number
  redeemedDate?: number
  auctionedDate?: number
  photos?: string[]              // Photo URLs
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const ItemCategorySchema = z.enum(['jewelry', 'gadget', 'appliance', 'vehicle', 'firearm', 'antique', 'other'])
export const ItemConditionSchema = z.enum(['excellent', 'good', 'fair', 'poor'])
export const ItemStatusSchema = z.enum(['pawned', 'redeemed', 'auctioned', 'expired', 'pulled_out'])

export const CreatePawnItemSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  category: ItemCategorySchema,
  description: z.string().min(1).max(500),
  brand: z.string().max(200).optional(),
  model: z.string().max(200).optional(),
  serialNumber: z.string().max(200).optional(),
  condition: ItemConditionSchema.default('good'),
  appraisalValue: z.number().min(0),
  pawnValue: z.number().min(0),
  weight: z.number().min(0).optional(),
  karat: z.number().min(0).max(24).optional(),
  notes: notesSchema.optional(),
})

export const ITEM_CATEGORY_LABELS: Record<ItemCategory, string> = {
  jewelry: 'Jewelry', gadget: 'Gadget', appliance: 'Appliance',
  vehicle: 'Vehicle', firearm: 'Firearm', antique: 'Antique', other: 'Other',
}
export const ITEM_CONDITION_LABELS: Record<ItemCondition, string> = {
  excellent: 'Excellent', good: 'Good', fair: 'Fair', poor: 'Poor',
}
export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  pawned: 'Pawned', redeemed: 'Redeemed', auctioned: 'Auctioned',
  expired: 'Expired', pulled_out: 'Pulled Out',
}

// ═══════════════════════════════════════════════════════════════
// Pawn Ticket (Aggregate Root)
// ═══════════════════════════════════════════════════════════════

export type TicketStatus = 'active' | 'renewed' | 'redeemed' | 'expired' | 'auctioned' | 'cancelled'
export type InterestType = 'monthly' | 'bi_monthly' | 'custom'

export interface PawnTicket {
  id: string                          // Aggregate root ID
  tenantId: string
  branchId?: string
  ticketNumber: string                // e.g. PT-20250701-0001
  customerId: string
  customerName?: string
  itemIds: string[]                   // Can have multiple items per ticket

  // ─── Loan Details ───────────────────────────────────────
  principalAmount: number             // Total loan granted
  interestRate: number                // Monthly interest rate (e.g. 3% = 0.03)
  interestType: InterestType
  advanceInterestMonths: number       // How many months interest paid upfront
  serviceCharge: number               // Processing fee
  netProceeds: number                 // principalAmount - advanceInterest - serviceCharge

  // ─── Dates ──────────────────────────────────────────────
  pawnDate: number
  maturityDate: number                // When loan is due
  expiryDate: number                  // When item goes to auction (maturity + grace period)
  gracePeriodDays: number
  renewedDate?: number
  redeemedDate?: number
  auctionedDate?: number

  // ─── Redemption ─────────────────────────────────────────
  totalRedemptionAmount: number       // principal + accrued interest - payments
  totalPaymentsMade: number
  remainingBalance: number

  // ─── Status ─────────────────────────────────────────────
  status: TicketStatus
  renewalCount: number
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const TicketStatusSchema = z.enum(['active', 'renewed', 'redeemed', 'expired', 'auctioned', 'cancelled'])
export const InterestTypeSchema = z.enum(['monthly', 'bi_monthly', 'custom'])

export const CreatePawnTicketSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  customerId: z.string().min(1),
  itemIds: z.array(z.string()).min(1),
  principalAmount: z.number().min(0),
  interestRate: z.number().min(0).max(1),
  interestType: InterestTypeSchema.default('monthly'),
  advanceInterestMonths: z.number().int().min(0).default(1),
  serviceCharge: z.number().min(0).default(0),
  gracePeriodDays: z.number().int().min(0).default(90),
  pawnDate: z.number().default(() => Date.now()),
  notes: notesSchema.optional(),
})

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  active: 'Active', renewed: 'Renewed', redeemed: 'Redeemed',
  expired: 'Expired', auctioned: 'Auctioned', cancelled: 'Cancelled',
}
export const INTEREST_TYPE_LABELS: Record<InterestType, string> = {
  monthly: 'Monthly', bi_monthly: 'Bi-Monthly', custom: 'Custom',
}

// ═══════════════════════════════════════════════════════════════
// Pawn Payment
// ═══════════════════════════════════════════════════════════════

export type PaymentType = 'interest' | 'principal' | 'renewal' | 'redemption' | 'partial'
export type PaymentMethod = 'cash' | 'gcash' | 'bank_transfer' | 'maya'

export interface PawnPayment {
  id: string
  tenantId: string
  ticketId: string
  ticketNumber?: string
  customerId: string
  customerName?: string
  paymentType: PaymentType
  amount: number
  paymentMethod: PaymentMethod
  referenceNumber?: string
  paymentDate: number
  interestPeriodCovered?: number      // For interest payments: which month
  remainingBalanceAfter: number
  receiptNumber?: string
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const PaymentTypeSchema = z.enum(['interest', 'principal', 'renewal', 'redemption', 'partial'])
export const PaymentMethodSchema = z.enum(['cash', 'gcash', 'bank_transfer', 'maya'])

export const CreatePawnPaymentSchema = z.object({
  tenantId: z.string().min(1),
  ticketId: z.string().min(1),
  customerId: z.string().min(1),
  paymentType: PaymentTypeSchema,
  amount: z.number().min(0),
  paymentMethod: PaymentMethodSchema.default('cash'),
  referenceNumber: z.string().max(100).optional(),
  paymentDate: z.number().default(() => Date.now()),
  notes: notesSchema.optional(),
})

export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  interest: 'Interest', principal: 'Principal', renewal: 'Renewal',
  redemption: 'Redemption', partial: 'Partial',
}
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash', gcash: 'GCash', bank_transfer: 'Bank Transfer', maya: 'Maya',
}

// ═══════════════════════════════════════════════════════════════
// Auction Item
// ═══════════════════════════════════════════════════════════════

export type AuctionStatus = 'pending' | 'listed' | 'sold' | 'unsold' | 'returned'

export interface AuctionItem {
  id: string
  tenantId: string
  ticketId: string
  ticketNumber?: string
  itemId: string
  itemDescription?: string
  originalAppraisalValue: number
  minimumBid: number
  soldAmount?: number
  auctionDate: number
  soldDate?: number
  buyerName?: string
  buyerContact?: string
  surplusReturned?: number        // Amount returned to original pawner after loan recovery
  status: AuctionStatus
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const AuctionStatusSchema = z.enum(['pending', 'listed', 'sold', 'unsold', 'returned'])

export const CreateAuctionItemSchema = z.object({
  tenantId: z.string().min(1),
  ticketId: z.string().min(1),
  itemId: z.string().min(1),
  minimumBid: z.number().min(0),
  auctionDate: z.number(),
  notes: notesSchema.optional(),
})

export const AUCTION_STATUS_LABELS: Record<AuctionStatus, string> = {
  pending: 'Pending', listed: 'Listed', sold: 'Sold', unsold: 'Unsold', returned: 'Returned',
}

// ═══════════════════════════════════════════════════════════════
// Query & Update Schemas
// ═══════════════════════════════════════════════════════════════

export const PawnCustomerQuerySchema = createQuerySchema({})
export const PawnItemQuerySchema = createQuerySchema({})
export const PawnTicketQuerySchema = createQuerySchema({})
export const PawnPaymentQuerySchema = createQuerySchema({})
export const AuctionItemQuerySchema = createQuerySchema({})

export const UpdatePawnCustomerSchema = createUpdateSchema({})
export const UpdatePawnItemSchema = createUpdateSchema({})
export const UpdatePawnTicketSchema = createUpdateSchema({})

// ═══════════════════════════════════════════════════════════════
// Metadata-Driven Configuration (per tenant)
// ═══════════════════════════════════════════════════════════════

export interface PawnshopConfig {
  /** Default monthly interest rate (e.g. 0.03 = 3%) */
  defaultInterestRate: number
  /** Maximum monthly interest rate allowed */
  maxInterestRate: number
  /** Default grace period after maturity before auction (days) */
  defaultGracePeriodDays: number
  /** Maximum grace period (days) */
  maxGracePeriodDays: number
  /** Maximum loan-to-appraisal ratio (e.g. 0.80 = 80%) */
  maxLoanToAppraisalRatio: number
  /** Service charge as percentage of principal */
  serviceChargeRate: number
  /** Allow partial redemption */
  allowPartialRedemption: boolean
  /** Allow ticket renewal */
  allowRenewal: boolean
  /** Max renewal count */
  maxRenewals: number
  /** Auction surplus handling: 'return' or 'keep' */
  auctionSurplusPolicy: 'return' | 'keep'
}

export const DEFAULT_PAWNSHOP_CONFIG: PawnshopConfig = {
  defaultInterestRate: 0.03,
  maxInterestRate: 0.05,
  defaultGracePeriodDays: 90,
  maxGracePeriodDays: 180,
  maxLoanToAppraisalRatio: 0.80,
  serviceChargeRate: 0.01,
  allowPartialRedemption: true,
  allowRenewal: true,
  maxRenewals: 3,
  auctionSurplusPolicy: 'return',
}
